import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@/lib/navigation";
import * as React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CredentialDialog, type Credential } from "@/components/credential-dialog";
import { PageHeader } from "@/components/layout-bits";
import { Button, Card, CardContent, Input, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { createTenant } from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";

const schema = z.object({
  slug: z
    .string()
    .regex(
      /^[a-z][a-z0-9-]{1,50}$/,
      "Lowercase letters, digits, hyphens; must start with a letter",
    ),
  display_name: z.string().min(1, "Enter a display name").max(255),
  admin_email: z.string().min(3, "Enter the admin's email").max(255),
  region: z.string().max(64).optional(),
});

export default function NewTenantPage() {
  const router = useRouter();
  const qc = useQueryClient();
  const [credential, setCredential] = React.useState<Credential | null>(null);
  const createdSlug = React.useRef<string | null>(null);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { slug: "", display_name: "", admin_email: "", region: "" },
  });

  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof schema>) =>
      createTenant({
        slug: v.slug,
        display_name: v.display_name,
        admin_email: v.admin_email,
        region: v.region?.trim() ? v.region.trim() : null,
      }),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: qk.tenants });
      createdSlug.current = res.tenant.slug;
      setCredential({ email: res.admin_email, password: res.admin_temp_password });
      toast.success(`Tenant “${res.tenant.slug}” provisioned`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="max-w-2xl">
      <PageHeader
        title="Onboard a tenant"
        description="Creates an isolated database and issues the first admin credential."
      />

      <Card>
        <CardContent className="pt-6">
          <form className="space-y-5" onSubmit={form.handleSubmit((v) => mutation.mutate(v))}>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" placeholder="acme" {...form.register("slug")} />
                <p className="text-xs text-muted-foreground">
                  Used in URLs &amp; the database name.
                </p>
                {form.formState.errors.slug && (
                  <p className="text-xs text-destructive">{form.formState.errors.slug.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="display_name">Display name</Label>
                <Input
                  id="display_name"
                  placeholder="Acme Corp"
                  {...form.register("display_name")}
                />
                {form.formState.errors.display_name && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.display_name.message}
                  </p>
                )}
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="admin_email">Admin email</Label>
                <Input
                  id="admin_email"
                  type="email"
                  placeholder="admin@acme.com"
                  {...form.register("admin_email")}
                />
                {form.formState.errors.admin_email && (
                  <p className="text-xs text-destructive">
                    {form.formState.errors.admin_email.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="region">Region (optional)</Label>
                <Input id="region" placeholder="us-east-1" {...form.register("region")} />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? "Provisioning…" : "Provision tenant"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => router.push("/tenants")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <CredentialDialog
        credential={credential}
        onClose={() => {
          setCredential(null);
          if (createdSlug.current) router.push(`/tenants/${createdSlug.current}`);
        }}
      />
    </div>
  );
}
